import { FC, useRef } from "react"
import CurrencyFormat from "react-currency-format"
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js"
import { Scatter } from "react-chartjs-2"
import ZoomPlugin from "chartjs-plugin-zoom"
import { InputField } from "../input/InputField"
import { getOutputData } from "../../utils/get-output-data"

export type OutputFormProps = {
  /**
   * Calculated response data to display to the user
   */
  responseData: any
}

export const OutputForm: FC<OutputFormProps> = ({ responseData }) => {
  const chartRef = useRef<Chart<any>>(null)

  if (!responseData?.outputs?.PDC) {
    return <div></div>
  }

  Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ZoomPlugin)

  const options = {
    responsive: true,
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: "Time (in Months)",
        },
      },
      y: {
        ticks: {
          callback: (value: any) => `${parseFloat((value * 100).toFixed(2))}%`,
        },
      },
    },
    plugins: {
      legend: {
        position: "top" as const,
      },
      zoom: {
        pan: {
          enabled: true,
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "xy" as const,
        },
      },
    },
  }

  const data = {
    datasets: [
      {
        showLine: true,
        label: "Conditional Probably of Default",
        data: getOutputData(responseData),
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.5)",
      },
    ],
  }

  return (
    <div className="flex flex-col items-center justify-center space-y-3">
      <div className="mb-8">
        <Scatter height={250} ref={chartRef} options={options} data={data} />
        <div className="flex justify-center">
          <button
            className="bg-brand-light rounded text-white py-2 px-8 mt-3 text-sm"
            type="button"
            onClick={() => chartRef.current?.resetZoom()}
          >
            Reset Zoom
          </button>
        </div>
      </div>

      <div className="mx-auto w-full max-w-sm">
        <label
          htmlFor={"ContractualMaturityDate"}
          className="block text-sm px-3 font-medium text-semibold text-gray-700"
        ></label>
        <div className="mx-auto w-full max-w-sm">
          <InputField
            id="ContractualMaturityDate"
            label="Contractual Maturity Date"
            type="date"
            value={responseData ? responseData.outputs.ContractualMaturityDate : ""}
            readOnly
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-sm">
        <label
          htmlFor={"ExpectedBehaviouralLife"}
          className="block text-sm px-3 font-medium text-semibold text-gray-700"
        >
          {"Expected Behavioural Life"}
        </label>
        <div className="mt-1">
          <CurrencyFormat
            thousandSeparator={true}
            decimalScale={2}
            readOnly
            id="ExpectedBehaviouralLife"
            value={responseData ? responseData.outputs.ExpectedBehaviouralLife : ""}
            displayType={"input"}
            className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-sm">
        <label htmlFor={"Final_ECL"} className="block text-sm px-3 font-medium text-semibold text-gray-700">
          {"Final ECL"}
        </label>
        <div className="mt-1">
          <CurrencyFormat
            thousandSeparator={true}
            decimalScale={2}
            prefix={"$"}
            readOnly
            id="Final_ECL"
            value={responseData ? responseData.outputs.Final_ECL : ""}
            displayType={"input"}
            className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-sm">
        <label htmlFor={"GrossCarryingAmount"} className="block text-sm px-3 font-medium text-semibold text-gray-700">
          {"Gross Carrying Amount"}
        </label>
        <div className="mt-1">
          <CurrencyFormat
            thousandSeparator={true}
            decimalScale={2}
            prefix={"$"}
            readOnly
            id="GrossCarryingAmount"
            value={responseData ? responseData.outputs.GrossCarryingAmount : ""}
            displayType={"input"}
            className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-sm">
        <label htmlFor={"ImpairmentRatio"} className="block text-sm px-3 font-medium text-semibold text-gray-700">
          {"Impairment Ratio"}
        </label>
        <div className="mt-1">
          <CurrencyFormat
            thousandSeparator={true}
            decimalScale={2}
            suffix={"%"}
            readOnly
            id="ImpairmentRatio"
            value={responseData ? responseData.outputs.ImpairmentRatio * 100 : ""}
            displayType={"input"}
            className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-sm">
        <label htmlFor={"PVContractual"} className="block text-sm px-3 font-medium text-semibold text-gray-700">
          {"Present Value of Contractual"}
        </label>
        <div className="mt-1">
          <CurrencyFormat
            thousandSeparator={true}
            decimalScale={2}
            prefix={"$"}
            readOnly
            id="PVContractual"
            value={responseData ? responseData.outputs.PVContractual : ""}
            displayType={"input"}
            className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-sm">
        <label htmlFor={"PVExpected"} className="block text-sm px-3 font-medium text-semibold text-gray-700">
          {"Present Value of Expected"}
        </label>
        <div className="mt-1">
          <CurrencyFormat
            thousandSeparator={true}
            decimalScale={2}
            prefix={"$"}
            readOnly
            id="PVExpected"
            value={responseData ? responseData.outputs.PVExpected : ""}
            displayType={"input"}
            className="block w-full h-10 rounded-md border-0 py-2 px-3 text-left shadow-lg transition focus:border-brand-light focus:ring-brand-light sm:text-sm"
          />
        </div>
      </div>
    </div>
  )
}

